<script lang="ts" setup>
import { ref, onMounted, computed } from "vue";

import { useTemplates } from "~/stores/template.store";
import { useBroadcast } from "~/stores/broadcast.store";
import { useContacts } from "~/stores/contacts.store";
import { useChatbots } from "~/stores/chatbots.store";
import { useChannels } from "~/stores/channel.store";
import { useToast } from "@profeat/ui-kit";
import { useI18n } from "vue-i18n";
import BroadcastV2VariablesForm from "../BroadcastV2ContentSettings/BroadcastV2VariablesForm.vue";
import BroadcastV2TemplatesForm from "../BroadcastV2ContentSettings/BroadcastV2TemplatesForm.vue";
import BroadcastSenderEmptyTemplate from "./BroadcastSenderEmptyTemplate.vue";

const templatesStore = useTemplates();
const broadcastStore = useBroadcast();
const contactsStore = useContacts();
const chatbotsStore = useChatbots();
const channelStore = useChannels();
const { showToast } = useToast();
const { t } = useI18n();

const isTemplatesListEmpty = computed(() => {
  return templatesStore.channelTemplates.length < 1;
});

const loading = ref(false);

onMounted(async () => {
  loading.value = true;

  await Promise.all([
    templatesStore.fetchChannelTemplates(),
    contactsStore.fetchFieldsWithoutValue(),
    chatbotsStore.fetchBotsByTemplate(),
  ]);

  if (channelStore.selectedWabaChannel) {
    contactsStore.filterEmptyContacts();
    if (!contactsStore.getSelectionsCount) {
      showToast({ color: "error", text: t("broadcast.errors.noCustomers") });
    }
  }

  broadcastStore.setSelectedTemplate(null);

  broadcastStore.loadStatistics();

  loading.value = false;
});
</script>

<template>
  <div class="loader-wrapper" v-if="loading">
    <BasePreloader class="preloader" />
  </div>

  <template v-else>
    <BroadcastSenderEmptyTemplate v-if="isTemplatesListEmpty" />
    <div class="broadcast-customization">
      <div class="content-settings">
        <BroadcastV2TemplatesForm />
        <BroadcastV2VariablesForm />
      </div>

      <div v-if="broadcastStore.getSelectedTemplate" class="content">
        <BroadcastV2MessagePreview />
        <BroadcastV2MessageTest />
      </div>
    </div>
  </template>
</template>
<style lang="scss" scoped>
.loader-wrapper {
  margin-top: 32px;
  width: 100%;
  height: 100%;
  position: relative;

  .preloader {
    margin: auto;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }
}

.broadcast-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;

  .broadcast-customization {
    display: flex;
    gap: 40px;
    overflow: hidden;
    height: 100%;

    .content-settings {
      width: 65%;
      display: flex;
      flex-direction: column;
      gap: 32px;
      max-height: 100%;
      overflow-y: auto;

      &::-webkit-scrollbar {
        width: 0;
      }
    }

    .content {
      width: 35%;
      display: flex;
      flex-direction: column;
      gap: 24px;
      overflow-y: auto;
      max-height: 100%;

      &::-webkit-scrollbar {
        width: 0;
      }
    }
  }

  .broadcast-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;

    box-sizing: border-box;
    height: 76px;
    padding: 16px 40px;
    border-top: 1px solid var(--common-rain);

    .action {
      width: 194px;
    }
  }
}
</style>
